import { PaletteOptions, ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    xl: React.CSSProperties;
    l: React.CSSProperties;
    m: React.CSSProperties;
    r: React.CSSProperties;
    s: React.CSSProperties;
    xs: React.CSSProperties;
    body3: React.CSSProperties
    body4: React.CSSProperties
    body5: React.CSSProperties
    body6: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    xl?: React.CSSProperties;
    l: React.CSSProperties;
    m: React.CSSProperties;
    r: React.CSSProperties;
    s: React.CSSProperties;
    xs: React.CSSProperties;
    body3: React.CSSProperties;
    body4: React.CSSProperties;
    body5: React.CSSProperties;
    body6: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    xl: true;
    l: true;
    m: true;
    r: true;
    s: true;
    xs: true;
    body3: true;
    body4: true;
    body5: true;
    body6: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    brand: true;
    accents: true;
  }
}

interface CommonThemePaletteOptions extends PaletteOptions {
  brand?: {
    primary: string,
    hover: string,
    pressed: string,
    snow: string,
    charcoal: string
  },
  accents?: {
    blue: string,
    green: string,
    red: string,
    yellow: string
  },
  text: {
    primary: string,
    secondary: string
    disabled: string
    tertiary: string
  },
  background: {
    default: string
    paper: string
    secondary: string
    border: string
    primary: string
    translucent: string
  }
}
export interface CommonThemeOptions extends ThemeOptions {
  palette: CommonThemePaletteOptions
}

export const customPaletteColors = {
  success: {
    main: '#97E3C2'
  },
  error: {
    main: '#FF3B30'
  },
  warning: {
    main: '#FFFEAD'
  },
  contrastDarkText: '#FFFFFF',
  contrastLightText: '#000000',
  brand: {
    primary: '#FCFF62',
    pressed: '#C0B835',
    hover: '#FFD700',
    snow: '#FFFFFF',
    charcoal: '#1F1F1A'
  },
  accents: {
    blue: '#3083FF',
    green: '#30FFC1',
    red: '#FF3830',
    yellow: '#FF9500'
  },
  dark: {
    text: {
      primary: '#FEFDEF',
      secondary: '#DADAC6',
      tertiary: '#9C9C8B',
      disabled: '#3C3B31',
    },
    background: {
      primary: '#1E1E22',
      secondary: '#26261E',
      tertiary: 'rgba(75, 75, 75, 0.5)',
      border: 'rgba(177, 255, 227, 0.2)',
      translucent: 'rgba(255, 255, 255, 0.12)'
    },
  },
  light: {
    text: {
      primary: '#101818',
      secondary: '#3E5555',
      tertiary: '#89A0A0',
      disabled: '#B8C3C3',
    },
    background: {
      primary: '#F0FFF9',
      secondary: '#D3FFEF',
      tertiary: '#A7FFDF',
      border: '#b1ffe333',
      translucent: 'rgba(255, 255, 255, 0.12)'
    },
  }
}

export const healthBarColors = {
  healthy: customPaletteColors.brand.primary,
  risky: customPaletteColors.accents.yellow,
  danger: customPaletteColors.accents.red
}

const constructTheme = (coloredTheme: 'dark' | 'light') => {
  const commonThemeOptions: CommonThemeOptions = {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1170,
        xl: 1400,
      },
    },
    shape: {
      borderRadius: 8
    },
    palette: {
      ...customPaletteColors,
      primary: {
        main: customPaletteColors.brand.primary, // main showing color
        light: customPaletteColors.brand.pressed, //lighter
        dark: customPaletteColors.brand.hover, //hover
        contrastText: customPaletteColors.contrastLightText
      },
      secondary: {
        main: customPaletteColors.brand.primary, // main showing color
        light: customPaletteColors.brand.pressed, //lighter
        dark: customPaletteColors.brand.hover, //hover
        contrastText: customPaletteColors.contrastDarkText
      },
      background: {
        default: customPaletteColors[coloredTheme].background.primary,
        paper: customPaletteColors[coloredTheme].background.tertiary,
        primary: customPaletteColors[coloredTheme].background.primary,
        secondary: customPaletteColors[coloredTheme].background.secondary,
        border: customPaletteColors[coloredTheme].background.border,
        translucent: customPaletteColors[coloredTheme].background.translucent
      },
      text: {
        primary: customPaletteColors[coloredTheme].text.primary,
        secondary: customPaletteColors[coloredTheme].text.secondary,
        tertiary: customPaletteColors[coloredTheme].text.tertiary,
        disabled: customPaletteColors[coloredTheme].text.disabled
      },
    },
    typography: {
      allVariants: {
        color: customPaletteColors[coloredTheme].text.primary,
        textTransform: 'none',
      },
      h1: {
        fontSize: '3rem',
        fontFamily: 'Proto Mono',
      },
      h2: {
        fontSize: '2rem',
        fontFamily: 'Proto Mono',
      },
      h3: {
        fontSize: '1.75rem',
        fontFamily: 'Proto Mono',
      },
      h4: {
        fontSize: '1.5rem',
        fontFamily: 'Proto Mono',
      },
      h5: {
        fontSize: '1.25rem',
        fontFamily: 'Proto Mono',
      },
      h6: {
        fontSize: '1.25rem',
        fontFamily: 'Proto Mono',
      },
      body1: {
        fontSize: '1.375rem',
        fontFamily: 'Proto Mono',
      },
      body2: {
        fontSize: '1rem',
        fontFamily: 'Proto Mono',
      },
      body3: {
        fontSize: '1.125rem',
        fontFamily: 'Proto Mono',
      },
      body4: {
        fontSize: '1rem',
        fontFamily: 'Proto Mono',
      },
      body5: {
        fontSize: '0.875rem',
        fontFamily: 'Proto Mono',
      },
      body6: {
        fontSize: '0.75rem',
        fontFamily: 'Proto Mono',
      },
      xl: {
        fontSize: '1.375rem',
        fontFamily: 'Inter',
      },
      l: {
        fontSize: '1.25rem',
        fontFamily: 'Inter',
      },
      m: {
        fontSize: '1.125rem',
        fontFamily: 'Inter',
      },
      r: {
        fontSize: '1rem',
        fontFamily: 'Inter',
      },
      s: {
        fontSize: '0.875rem',
        fontFamily: 'Inter',
      },
      xs: {
        fontSize: '0.75rem',
        fontFamily: 'Inter',
      },

    },
    components: {
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: customPaletteColors[coloredTheme].text.primary
          }
        }
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: customPaletteColors[coloredTheme].text.primary
          }
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        },
        styleOverrides: {
          root: () => ({
            ":disabled": {
              color: customPaletteColors[coloredTheme].text.disabled
            }
          }),
        }
      },
      MuiButton: {
        defaultProps: {
          disableRipple: true
        }
      },
    }
  }
  return commonThemeOptions
}

export default constructTheme